import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AffiliateEarnings } from "../../components/affiliate/AffiliateEarnings";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const ERR = {
  valid_vat_number_required: "Please enter a valid VAT number.",
  uk_bank_details_required: "UK bank details need a 6-digit sort code and 8-digit account number.",
  valid_iban_required: "Please enter a valid IBAN.",
  self_billing_consent_required: "Please tick the self-billing agreement.",
};
const EMPTY = { legal_name: "", entity_type: "individual", country: "GB", address: "", vat_registered: false, vat_number: "",
  account_name: "", sort_code: "", account_number: "", iban: "", bic: "", self_billing_consent: false };

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-[#b9ad8f]">{label}</span><div className="mt-1.5">{children}</div></label>
);
const inputCls = "w-full rounded-lg bg-[#1a201d] border border-[#3a3f35] px-3 py-2.5 text-[#f3ecd9] focus:outline-none focus:border-[#d7bb7a] transition-colors";
const In = ({ k, f, set, ...p }) => <input className={inputCls} value={f[k] || ""} onChange={(e) => set({ ...f, [k]: e.target.value })} data-testid={`aff-tax-${k}`} {...p} />;

const BankFields = ({ f, set }) => (f.country === "GB" ? (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Sort code"><In k="sort_code" f={f} set={set} inputMode="numeric" placeholder="12-34-56" /></Field>
    <Field label="Account number"><In k="account_number" f={f} set={set} inputMode="numeric" placeholder={f.account_last4 ? `••••${f.account_last4} (leave blank to keep)` : "8 digits"} /></Field>
  </div>
) : (
  <div className="grid grid-cols-3 gap-3">
    <div className="col-span-2"><Field label="IBAN"><In k="iban" f={f} set={set} placeholder={f.iban_last4 ? `••••${f.iban_last4}` : ""} /></Field></div>
    <Field label="BIC / SWIFT"><In k="bic" f={f} set={set} /></Field>
  </div>
));

const Gate = ({ text }) => (
  <div className="min-h-screen bg-[#0f1412] text-[#f3ecd9] flex items-center justify-center p-6" data-testid="aff-tax-gate">
    <p className="max-w-sm text-center text-[#cfc6ae]">{text}</p>
  </div>
);

export default function AffiliateDetailsPage() {
  const [me, setMe] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/affiliate-tax/me`, auth()).then((r) => {
      setMe(r.data);
      if (r.data.profile) setF({ ...EMPTY, ...r.data.profile, vat_number: r.data.profile.vat_number || "", self_billing_consent: false });
    }).catch(() => setMe({ approved: false }));
  }, []);
  if (!me) return <Gate text="Loading…" />;
  if (!me.approved) return <Gate text="This page is for approved Soulumo affiliates and partners. If you've been invited, ask us to approve your account." />;
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api/affiliate-tax/me`, f, auth());
      setF({ ...EMPTY, ...r.data.profile, vat_number: r.data.profile.vat_number || "", self_billing_consent: false });
      setMe({ ...me, profile: r.data.profile });
      toast.success("Your details are saved");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(ERR[d] || "Please check the form and try again.");
    } finally { setBusy(false); }
  };
  const rate = me.role === "partner" ? "5% recurring override" : "25% recurring commission";
  return (
    <div className="min-h-screen bg-[#0f1412] text-[#f3ecd9] px-5 py-12" data-testid="aff-tax-page">
      <div className="mx-auto max-w-xl">
        <p className="text-xs uppercase tracking-[0.3em] text-[#d7bb7a]">Soulumo {me.role}</p>
        <h1 className="mt-3 text-4xl sm:text-5xl text-[#f3ecd9]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Your payout details</h1>
        <p className="mt-4 text-sm text-[#cfc6ae]" data-testid="aff-tax-rate-copy">
          Earn {rate} on qualifying Soulumo subscriptions you refer. Percentages are worked out on subscription revenue excluding VAT and sales taxes, and are subject to the programme terms, including refunds and chargebacks. Paid monthly by bank transfer.
        </p>
        <AffiliateEarnings />
        {me.profile && <p className="mt-3 text-xs text-[#9fb39a]" data-testid="aff-tax-saved-note">Details on file since {me.profile.created_at?.slice(0, 10)}. Saving again replaces them.</p>}
        <div className="mt-10 space-y-5">
          <Field label="Legal name (person or business)"><In k="legal_name" f={f} set={setF} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="I am">
              <select className={inputCls} value={f.entity_type} onChange={(e) => setF({ ...f, entity_type: e.target.value })} data-testid="aff-tax-entity_type">
                <option value="individual">An individual</option><option value="business">A business</option>
              </select>
            </Field>
            <Field label="Country (2 letters)"><In k="country" f={f} set={(v) => setF({ ...v, country: v.country.toUpperCase().slice(0, 2) })} placeholder="GB" /></Field>
          </div>
          <Field label="Address"><In k="address" f={f} set={setF} /></Field>
          <label className="flex items-center gap-3 text-sm" data-testid="aff-tax-vat-toggle">
            <input type="checkbox" checked={f.vat_registered} onChange={(e) => setF({ ...f, vat_registered: e.target.checked })} className="h-4 w-4 accent-[#d7bb7a]" />
            I am VAT registered
          </label>
          {f.vat_registered && <Field label="VAT number"><In k="vat_number" f={f} set={setF} placeholder="GB123456789" /></Field>}
          <Field label="Name on bank account"><In k="account_name" f={f} set={setF} /></Field>
          <BankFields f={f} set={setF} />
          <label className="flex items-start gap-3 text-sm text-[#cfc6ae]" data-testid="aff-tax-consent">
            <input type="checkbox" checked={f.self_billing_consent} onChange={(e) => setF({ ...f, self_billing_consent: e.target.checked })} className="mt-1 h-4 w-4 accent-[#d7bb7a]" />
            <span>I agree to self-billing: Soulumo will create my monthly commission invoice on my behalf, and I won't issue my own invoices for the same commission. I'll tell Soulumo straight away if my VAT status or details change. (Terms {me.terms_version})</span>
          </label>
          <button type="button" onClick={save} disabled={busy}
            className="w-full rounded-full border border-[#d7bb7a] bg-[#d7bb7a]/15 py-3.5 text-xs uppercase tracking-[0.28em] hover:bg-[#d7bb7a]/25 transition-colors disabled:opacity-50"
            data-testid="aff-tax-save-btn">{busy ? "Saving…" : "Save my details"}</button>
        </div>
      </div>
    </div>
  );
}
